import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useSendDocumentWhatsApp } from "../../../hooks/api/projectDocumentsApiHooks";

/**
 * Modal para enviar documento via WhatsApp (automaticamente ao integrador)
 * @param {object} props
 * @param {boolean} props.isOpen - Se o modal está aberto
 * @param {function} props.onClose - Callback ao fechar
 * @param {object} props.document - Documento a ser enviado (ProjectDocumentResponseDTO)
 * @param {object} props.project - Dados do projeto (para pegar o integrador)
 * @param {function} props.onSuccess - Callback ao sucesso
 */
export default function SendDocumentWhatsAppModal({ isOpen, onClose, document, project, onSuccess }) {
  const { mutateAsync: sendDocument, isLoading } = useSendDocumentWhatsApp();

  // Buscar dados do integrador do projeto
  const integradorName = project?.clients?.name || "Integrador";
  const integradorPhone = project?.clients?.phone

  // Handler de envio automático
  const handleSend = async () => {
    if (!integradorPhone) {
      toast.error("Integrador não possui telefone cadastrado no projeto");
      return;
    }

    try {
      await sendDocument({
        documentId: document.id,
        recipientPhone: integradorPhone,
        recipientName: integradorName,
      });

      toast.success("Documento enviado via WhatsApp com sucesso!");
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Erro ao enviar documento:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao enviar documento via WhatsApp";
      toast.error(errorMessage);
    }
  };

  // Enviar automaticamente ao abrir (opcional - comentar se quiser confirmação manual)
  useEffect(() => {
    if (isOpen && integradorPhone) {
      // Descomentar a linha abaixo para envio automático sem confirmação
      // handleSend();
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={!isLoading ? onClose : undefined} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WhatsAppIcon color="success" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Enviar via WhatsApp
          </Typography>
        </Box>
        {!isLoading && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {/* Informações do Documento */}
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: "background.default",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Documento:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {document?.documentType?.name || "Desconhecido"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            📄 {document?.fileName}
          </Typography>
        </Box>

        {/* Informações do Destinatário */}
        {integradorPhone ? (
          <Box
            sx={{
              p: 1.5,
              bgcolor: "success.lighter",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "success.main",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              📱 Destinatário: {integradorName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Telefone: {integradorPhone}
            </Typography>
          </Box>
        ) : (
          <Alert severity="error">
            <Typography variant="body2">
              ⚠️ Integrador não possui telefone cadastrado no projeto.
            </Typography>
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Enviando documento...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSend}
          disabled={!integradorPhone || isLoading}
          startIcon={<WhatsAppIcon />}
        >
          {isLoading ? "Enviando..." : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
